import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

import { ErrorMessage } from '../components/ErrorMessage';
import { LoadingPage } from '../components/LoadingPage';
import { getBaseUrl } from '../services/jira';

interface Context {
  baseUrl: string;
}

const JiraContext = React.createContext<Context | undefined>(undefined);

export function JiraContextProvider({ children }: Readonly<PropsWithChildren>) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [context, setContext] = useState<Context>();

  useEffect(() => {
    void (async () => {
      try {
        const baseUrl = await getBaseUrl();
        setContext({ baseUrl });
      } catch (e) {
        console.error(e);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  if (isLoading) {
    return <LoadingPage />;
  } else if (error) {
    return (
      <ErrorMessage
        title="Error loading jira context"
        text="An error occurred while attempting to load jira context."
      />
    );
  } else {
    return (
      <JiraContext.Provider value={context}>{children}</JiraContext.Provider>
    );
  }
}

export function useJiraContext() {
  const context = useContext(JiraContext);
  if (!context) {
    throw new Error(
      'Jira context not initialized, probably context provider is missing'
    );
  }
  return context;
}
